import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { BlogRelatedPostCard } from "@/components/blog/BlogRelatedPostCard";
import type { Locale } from "@/context/AppProviders";
import { useHorizontalScrollOverflow } from "@/hooks/useHorizontalScrollOverflow";
import { fetchBlogRelated } from "@/lib/blogApi";
import { blogPostPath } from "@/lib/routes";
import { cn } from "@/lib/utils";
import type { BlogListItem } from "@/types/blog";

type BlogRelatedPostsProps = {
  slug: string;
  locale: Locale;
};

export function BlogRelatedPosts({ slug, locale }: BlogRelatedPostsProps) {
  const isTr = locale === "tr";
  const scrollRef = useRef<HTMLDivElement>(null);
  const [posts, setPosts] = useState<BlogListItem[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const rafRef = useRef(0);

  const { canScrollLeft, canScrollRight } = useHorizontalScrollOverflow(scrollRef, [posts.length]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      const result = await fetchBlogRelated(slug, { limit: 8 });
      if (cancelled) return;

      if (result.ok && result.data) {
        setPosts(result.data.items);
      } else {
        setPosts([]);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const updateActiveIndex = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const cards = el.querySelectorAll<HTMLElement>(".blog-related-card");
    if (cards.length === 0) {
      setActiveIndex(0);
      return;
    }

    const scrollLeft = el.scrollLeft;
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    cards.forEach((card, index) => {
      const distance = Math.abs(card.offsetLeft - scrollLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    setActiveIndex(closestIndex);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || posts.length === 0) return;

    const scheduleActiveIndex = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0;
        updateActiveIndex();
      });
    };

    scheduleActiveIndex();
    el.addEventListener("scroll", scheduleActiveIndex, { passive: true });
    window.addEventListener("resize", scheduleActiveIndex);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      el.removeEventListener("scroll", scheduleActiveIndex);
      window.removeEventListener("resize", scheduleActiveIndex);
    };
  }, [posts.length, updateActiveIndex]);

  const scrollByCard = useCallback((direction: -1 | 1) => {
    const el = scrollRef.current;
    if (!el) return;
    const card = el.querySelector<HTMLElement>(".blog-related-card");
    if (!card) return;
    const gap = Number.parseFloat(getComputedStyle(el).gap) || 20;
    el.scrollBy({ left: direction * (card.offsetWidth + gap), behavior: "smooth" });
  }, []);

  const scrollToIndex = useCallback((index: number) => {
    const el = scrollRef.current;
    if (!el) return;
    const card = el.querySelectorAll<HTMLElement>(".blog-related-card")[index];
    if (!card) return;
    el.scrollTo({ left: card.offsetLeft - el.offsetLeft, behavior: "smooth" });
  }, []);

  if (posts.length === 0) {
    return null;
  }

  return (
    <section className="blog-related-posts fade-up mx-auto mt-12 max-w-none sm:mt-14" aria-labelledby="related-posts-title">
      <h2
        id="related-posts-title"
        className="mx-auto mb-8 max-w-3xl text-2xl font-bold tracking-tight text-[var(--text)] sm:mb-10 sm:text-[1.75rem]"
      >
        {isTr ? "Diğer yazılar" : "Other posts"}
      </h2>

      <div className="blog-related-viewport relative">
        {canScrollLeft ? (
          <button
            type="button"
            onClick={() => scrollByCard(-1)}
            className={cn(
              "absolute left-3 top-[42%] z-10 hidden size-11 -translate-y-1/2 items-center justify-center rounded-full sm:flex lg:left-6",
              "border border-[var(--border)] bg-[var(--card)] text-[var(--text)]",
              "shadow-[0_4px_24px_-6px_rgba(0,0,0,0.18)] transition hover:scale-105 hover:border-enabase-blue/25"
            )}
            aria-label={isTr ? "Önceki yazılar" : "Previous posts"}
          >
            <ChevronLeft className="size-5" strokeWidth={2} />
          </button>
        ) : null}

        <div
          ref={scrollRef}
          className="blog-related-track flex snap-x snap-proximity gap-4 overflow-x-auto scroll-smooth overscroll-x-contain pb-1 sm:gap-5 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        >
          {posts.map((post) => (
            <BlogRelatedPostCard
              key={post.id}
              post={post}
              href={blogPostPath(locale, post.slug)}
              locale={locale}
            />
          ))}
        </div>

        {canScrollRight ? (
          <button
            type="button"
            onClick={() => scrollByCard(1)}
            className={cn(
              "absolute right-3 top-[42%] z-10 hidden size-11 -translate-y-1/2 items-center justify-center rounded-full sm:flex lg:right-6",
              "border border-[var(--border)] bg-[var(--card)] text-[var(--text)]",
              "shadow-[0_4px_24px_-6px_rgba(0,0,0,0.18)] transition hover:scale-105 hover:border-enabase-blue/25"
            )}
            aria-label={isTr ? "Sonraki yazılar" : "Next posts"}
          >
            <ChevronRight className="size-5" strokeWidth={2} />
          </button>
        ) : null}
      </div>

      {posts.length > 1 ? (
        <div className="mt-6 flex justify-center gap-2">
          {posts.map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => scrollToIndex(index)}
              className={cn(
                "size-2.5 rounded-full transition",
                activeIndex === index ? "bg-enabase-blue" : "bg-[var(--border)] hover:bg-enabase-blue/40"
              )}
              aria-label={isTr ? `${index + 1}. yazı` : `Post ${index + 1}`}
              aria-current={activeIndex === index ? "true" : undefined}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}
